import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, DatePicker, Select } from "antd";
import ReportKpiStrip from "./ReportKpiStrip";
import ReportRankedBreakdown from "./ReportRankedBreakdown";
import DetailSearchTable from "./DetailSearchTable";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Composes the page header + filter bar + ReportKpiStrip + the report's chart
// card + DetailSearchTable, driven by a single per-report config
// (reportConfigs.js). Does not fetch data itself — the parent *IndComp page
// still owns its own single LoadExtendedDiamondChartReports fetch and passes
// the resulting slice down as `report`, same cardinality as before.
const ReportScaffold = ({
  config,
  report,
  loading,
  dealerId,
  forDealer,
  dealerCompany,
  globalDiamonds,
  onDateChange,
  onDiamondTypeChange,
  onApplyFilter,
  refetchToken,
  onDownloadChart,
  downloadingChart,
  detailTable,
  sidePanel,
  hideChart,
  insight,
  extraFilterFields,
  onChartClick,
}) => {
  const { t } = useTranslation();
  const ChartCard = config.ChartCard;

  return (
    <React.Fragment>
      <div className="pendjewelreq__section form__fields">
        <div className="row magento__mapping">
          <div className="col-md-12">
            <div className="edr-pagehead-row">
              <div className="edr-pagehead">
                <h1 className="edr-pagehead-title">{t(config.pageTitle)}</h1>
                <div className="edr-pagehead-sub">
                  {moment(globalDiamonds.dataRangeFrom).format("MMM D, YYYY")}
                  {" – "}
                  {moment(globalDiamonds.dataRangeTo).format("MMM D, YYYY")}
                </div>
              </div>
              <div className="edr-pagehead-actions">
                {onDownloadChart && report?.[config.dataKey]?.length > 0 && (
                  <Button className="ghost-btn" onClick={onDownloadChart} loading={downloadingChart}>
                    <i className="fa fa-download"></i> {t(hideChart ? "Download PDF" : "Download Chart")}
                  </Button>
                )}
                <button className="primary-btn" onClick={onApplyFilter}>
                  {t("Apply Filter")}
                </button>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="row edr-filters">
              <div className="col-md-3">
                <div className="input__block dataRange-input-block datepicker__block">
                  <label>{t("Date Range")}</label>
                  <RangePicker
                    className="date-range-input"
                    ranges={{
                      Today: [moment(), moment()],
                      Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
                      "Last 7 Days": [moment().subtract(7, "days"), moment()],
                      "Last 30 Days": [moment().subtract(30, "days"), moment()],
                      "This Month": [moment().startOf("month"), moment().endOf("month")],
                      "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "months").endOf("month")],
                      "Last 90 Days": [moment().subtract(90, "days"), moment()],
                      "Year To Date": [moment().startOf("year"), moment()],
                    }}
                    format={"MM-DD-YYYY"}
                    onChange={onDateChange}
                    defaultValue={[moment().subtract(30, "days"), moment()]}
                    defaultPickerValue={[moment().subtract(30, "days"), moment()]}
                  />
                </div>
              </div>

              {extraFilterFields !== undefined ? (
                extraFilterFields
              ) : (
                <div className="col-md-3">
                  <div className="input__block">
                    <label>{t("Select Diamond")}</label>
                    <Select
                      className="border__grey"
                      placeholder={t("All")}
                      showSearch
                      name="SelectedDiamondValue"
                      optionFilterProp="children"
                      value={globalDiamonds.SelectedDiamondValue || null}
                      onChange={(e) => onDiamondTypeChange(e, "SelectedDiamondValue")}
                    >
                      <Option value="All">{t("All")}</Option>
                      <Option value="Mined">{t("Mined Diamond")}</Option>
                      <Option value="Lab">{t("Lab Grown Diamond")}</Option>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="col-md-12">
            <ReportKpiStrip report={report} loading={loading} config={config} />
          </div>

          {insight && <div className="col-md-12">{insight}</div>}

          {hideChart ? (
            <div className="col-md-12" id="pdf-content">
              {sidePanel !== undefined ? (
                sidePanel
              ) : (
                <ReportRankedBreakdown data={report?.[config.dataKey]} loading={loading} config={config} />
              )}
            </div>
          ) : (
            <div className="col-md-12">
              <div className="edr-grid2">
                <div id="pdf-content">
                  <ChartCard data={report?.[config.dataKey]} loading={loading} onSliceClick={onChartClick} />
                </div>
                {sidePanel !== undefined ? (
                  sidePanel
                ) : (
                  <ReportRankedBreakdown data={report?.[config.dataKey]} loading={loading} config={config} />
                )}
              </div>
            </div>
          )}

          <div className="col-md-12">
            {detailTable !== undefined ? (
              detailTable
            ) : (
              <DetailSearchTable
                dealerId={dealerId}
                forDealer={forDealer}
                dataRangeFrom={globalDiamonds.dataRangeFrom}
                dataRangeTo={globalDiamonds.dataRangeTo}
                selectedDiamondValue={globalDiamonds.SelectedDiamondValue}
                reportName={config.detail.reportName}
                filterParamName={config.detail.filterParamName}
                filterValue=""
                fileNamePart={config.detail.fileNamePart}
                dealerCompany={dealerCompany}
                refetchToken={refetchToken}
                title={t("Recent {{dim}} Searches", { dim: t(config.dimensionLabel) })}
              />
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ReportScaffold;
